import { differenceInYears } from "date-fns";
import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import { selectLocation } from "src/selectors/application";
import {
  Applicant,
  Asset,
  Debt,
  IncomeSource,
  MortgageInformation,
  OtherOwnedProperty,
} from "src/types/Applicant";

import { entities, formatters, notUndefinedOrNull, schemas, verify } from "@fraction/shared";
import _ from "lodash";

interface SharedItem {
  owners: Applicant[];
}

type ShareableItem = entities.FinancialAsset | entities.Debt | entities.Income | entities.Property;

function selectSharedItems<T extends ShareableItem, R extends object>(
  applicants: entities.ApplicantT[],
  { key, selector }: { key: "assets" | "debts" | "incomes" | "properties"; selector: (obj: T) => R }
): Array<SharedItem & R> {
  return Object.values(
    applicants
      ?.map((applicant) =>
        applicant[key]?.map((obj: ShareableItem) => ({
          obj,
          owner: selectApplicant(applicant),
        }))
      )
      .flat()
      .filter(notUndefinedOrNull)
      .reduce((objMap, container) => {
        const id = container.obj.id;
        if (!id) {
          return objMap;
        }
        if (objMap[id]) {
          objMap[id].owners.push(container.owner);
        } else {
          objMap[id] = {
            owners: [container.owner],
            ...selector(container.obj as T),
          };
        }
        return objMap;
      }, {} as Record<string, SharedItem & R>)
  );
}

/** Something to consider is that when we are actively on a screen that has multiple items
 * (i.e. an overview screen) we probably don't want to do a save and update for the inner items
 * because there is a certain order to them which we can't guarantee will be returned
 * (it probably will be, but you never know).
 * We should also think about this in the context of the applicants array - we might want to use
 * the emails to know the location of each applicant object.
 * Assuming we are fine with reordering once a user has passed that page, we don't need to maintain
 * the ItemControl object either, which is nice.
 */
export const selectApplicationFlowState = (
  application: entities.ApplicationT | schemas.Application
): ApplicationFlowState => {
  const assets = selectSharedItems(application.applicants || [], { key: "assets", selector: selectAsset });
  const debts = selectSharedItems(application.applicants || [], {
    key: "debts",
    selector: selectDebt,
  }).filter(
    ({ debtTypeAndLenderName }) =>
      debtTypeAndLenderName?.type && !verify.isMortgageType(debtTypeAndLenderName?.type)
  );
  // filter out the primary property
  const otherOwnedProperties = selectSharedItems(application.applicants || [], {
    key: "properties",
    selector: selectOtherOwnedProperty,
  }).filter(({ id }) => id !== application.property?.id);
  const incomeSources = selectSharedItems(application.applicants || [], {
    key: "incomes",
    selector: selectIncome,
  });

  return {
    id: application.id,
    propertyLocation: selectLocation(application.property),
    propertyValue: formatters.number.getCurrencyString(application.property?.valuations?.[0]?.value),
    amountRequested: application.requestedAmount,
    creditScoreGuess: application.creditScoreGuess,
    propertyType: {
      propertyType: application.property?.type,
      propertyUsage: application.property?.usage,
    },
    useOfFunds: {
      choices: application.useOfFundsChoices,
      description: application.useOfFundsDescription,
    },
    applicants: _.sortBy(
      application.applicants?.map((applicant, index) => selectApplicant(applicant)),
      "priority"
    ),
    primaryPropertyMortgages: application.property?.debts?.map((debt, index) =>
      selectMortgageInformation(debt)
    ),
    assets: { items: assets },
    debts: { items: debts },
    otherOwnedProperties: { items: otherOwnedProperties },
    incomeSources: { items: incomeSources },
  };
};

export const selectApplicant = (applicant: entities.ApplicantT): Applicant => ({
  id: applicant.id,
  userId: applicant.user?.id,
  name: formatters.user.userName(applicant.user),
  email: {
    email: applicant.user?.email,
    optedIn: applicant.user?.optedInToEmailsAt,
  },
  phoneAndContactMethodPreferences: {
    phone: applicant.user?.phone
      ? formatters.phone.formatStrippedPhoneNumber(applicant.user?.phone, {
          stripInternationalCode: true,
          stripInternationalCodeNotation: true,
        })
      : undefined,
    contactMethodPreferences: applicant.user?.contactMethodPreferences,
  },
  address: selectLocation(applicant.homeAddress),
  race: applicant.race,
  ethnicity: applicant.ethnicity,
  sex: applicant.sex,
  militaryStatus: applicant.militaryStatus,
  maritalStatus: {
    maritalStatus: applicant.maritalStatus,
  },
  socialXNumber: applicant.socialXNumber || undefined,
  residencyStatus: applicant.residencyStatus,
  employmentStatus: applicant.employmentStatus,
  bankruptcyStatus: applicant.bankruptcyStatus,
  timeSinceBankruptcy: applicant.lastBankruptcy
    ? Math.abs(differenceInYears(new Date(applicant.lastBankruptcy) || new Date(), new Date()))
    : undefined,
  dateOfBirth: applicant.user?.dateOfBirth ? new Date(applicant.user?.dateOfBirth) : undefined,
  waCivilUnionAddendum: {
    connectedToName: applicant.connectedToName,
    waCivilUnionAddendum: applicant.waCivilUnionAddendum,
  },
  priority: applicant.priority,
});

export const selectAsset = (asset: entities.FinancialAsset): Asset => ({
  id: asset.id,
  assetDetails: {
    type: asset.type,
    description: asset.description,
    value: formatters.number.getCurrencyString(asset.value),
  },
});

export const selectDebt = (debt: entities.Debt): Debt => ({
  id: debt.id,
  debtTypeAndLenderName: {
    lenderName: debt.withWho,
    type: debt.type,
  },
  debtBalanceAndPayments: {
    balance: formatters.number.getCurrencyString(debt.currentlyOwing),
    paymentSize: formatters.number.getCurrencyString(debt.payment?.amount),
    frequency: debt.payment?.frequency,
    payOffEveryMonth: false,
  },
});

export const selectIncome = (income: entities.Income): IncomeSource => ({
  id: income.id,
  incomeSourceType: income.type,
  employerDetails: {
    name: income.employerName,
    location: selectLocation(income.employerAddress),
  },
  incomeDetails: {
    income: formatters.number.getCurrencyString(income.payment?.amount),
    jobTitle: income.jobTitle,
    employmentType: income.employmentType,
  },
});

export const selectMortgageInformation = (debt: entities.Debt): MortgageInformation => ({
  id: debt.id,
  mortgageRateAndType: {
    currentRate: formatters.number.getCurrencyString(debt.rate),
    rateType: debt.rateType,
    mortgageType: debt.type,
  },
  mortgageBalanceAndLender: {
    amount: formatters.number.getCurrencyString(debt.currentlyOwing),
    lenderName: debt.withWho,
  },
  mortgagePayments: {
    frequency: debt.payment?.frequency,
    paymentAmount: formatters.number.getCurrencyString(debt.payment?.amount),
  },
});

export const selectOtherOwnedProperty = (property: entities.Property): OtherOwnedProperty => ({
  id: property.id,
  propertyLocation: selectLocation(property),
  propertyValue: formatters.number.getCurrencyString(
    property.valuations?.[property.valuations?.length - 1]?.value
  ),
  propertyType: {
    propertyType: property.type,
    propertyUsage: property.usage,
  },
  rentalIncome: formatters.number.getCurrencyString(property.incomes?.[0]?.payment?.amount),
  planToSellDuringProcess: property.planToSell,
  mortgages: { items: property.debts?.map(selectMortgageInformation) || [] },
});
